import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { JIRATransformer } from '@atlaskit/editor-jira-transformer';

import { TestExecution } from '@qality/common-model';
import {
  DateFormatService,
  StorageOfUniqueServices,
} from '@qality/common-service';

type listTypes = 'numberType' | 'bulletType';

export default {
  getDescription: async (
    testExecution: TestExecution,
    reportedTestExecutionStepId: number
  ): Promise<string> => {
    const link = await getLinkForDescription(
      testExecution.testCaseIssue.fields.summary,
      testExecution.id,
      testExecution.createdDate
    );
    const reproSteps = getReproStepsForDescription(
      testExecution,
      reportedTestExecutionStepId
    );

    if (!reproSteps) {
      return link;
    }

    return `${link}\n${reproSteps}`;
  },
};

const getLinkForDescription = async (
  testCaseSummary: string,
  testExecutionId: number,
  testExecutionCreatedDate: string
) => {
  const urlService = StorageOfUniqueServices.get('URLService');
  const testExecutionUrl = await urlService.getTestCaseExecutionUrl(
    testExecutionId
  );
  const formattedTestExecutionCreatedDate = DateFormatService.formatDate(
    testExecutionCreatedDate,
    'longDateFormat'
  );

  return `Issue discovered while executing:\n
  *Test Case:* ${testCaseSummary}\n
  *Execution Date:* ${formattedTestExecutionCreatedDate}\n
  [Open Execution|${testExecutionUrl}]\n
  ----\n`;
};

const getReproStepsForDescription = (
  testExecution: TestExecution,
  reportedTestExecutionStepId: number
) => {
  const failedStepIndex = testExecution.testSteps.findIndex(
    (step) => step.testExecutionStepId === reportedTestExecutionStepId
  );

  if (failedStepIndex === -1) {
    return '';
  }

  const reproductionSteps = testExecution.testSteps.slice(
    0,
    failedStepIndex + 1
  );

  return [
    `*Reproduction steps*`,
    ...reproductionSteps.map((testStep, index) => {
      const stepName = `${index + 1}. ${parseFromHtmlToMarkdown(
        testStep.step
      )}`;

      if (testStep.status.category !== 'FAILED') {
        return stepName;
      }

      const failedStep = [`${stepName} *${testStep.status.name}*`];
      testStep.result &&
        failedStep.push(
          `*Expected:* ${parseFromHtmlToMarkdown(testStep.result)}`
        );
      testStep.comment &&
        failedStep.push(
          `*Comment:* ${parseFromHtmlToMarkdown(testStep.comment)}`
        );

      return failedStep.join('\n');
    }),
  ].join('\n');
};

const jiraTransformer = new JIRATransformer(defaultSchema);

export const parseFromHtmlToMarkdown = (text: string) => {
  if (!text) {
    return '';
  }

  const textLines = text
    .replaceAll(/(<\/?strong>)/g, '*') // match <strong> and </strong>
    .replaceAll(/(<\/?em>)/g, '_') // match <em> and </em>
    .replaceAll(/(<\/?u>)/g, '+') // match <u> and </u>
    .replaceAll(/(<\/?strike>)/g, '-') // match <strike> and </strike>
    .replaceAll('</p><p>', '</p>\n<p>') // split paragraphs into new lines
    .replaceAll('</p><ul>', '</p>\n<ul>') // split lists into new lines
    .replaceAll('</ul><p>', '</ul>\n<p>') // split lists into new lines
    .replaceAll(/<\/p><ol[^>]*>/g, '</p>\n<ol>') // split lists into new lines
    .replaceAll('</ol><p>', '</ol>\n<p>') // split lists into new lines
    .replaceAll(/<ol[^>]*>/g, '<ol>') // do not store type for ordered list
    .split('\n');

  const textLinesWithBulletPoints = textLines
    .map((line) => {
      if (line.startsWith('<ul>') || line.startsWith('<ol>')) {
        const listType = line.startsWith('<ol>') ? 'numberType' : 'bulletType';
        return replaceBulletPointsToJiraSchema(line, listType);
      }

      return line;
    })
    .flat();

  // to have proper indentation we need to add a blank line under any bullet/number list
  const shouldAddBlankLine = textLines.some((textLine) =>
    textLine.includes('<li>')
  );

  if (shouldAddBlankLine) {
    textLinesWithBulletPoints.push('');
  }

  const parsedTextLines = textLinesWithBulletPoints.map((line) =>
    jiraTransformer.parse(line).textContent.trim()
  );

  return parsedTextLines.join('\n');
};

/* 
  We provide not valid HTML schema for bullet points, for that reason manual transformation
  to JIRA schema needs to be done
*/
const replaceBulletPointsToJiraSchema = (text: string, listType: listTypes) => {
  const openingMarkup = listType === 'numberType' ? '<ol>' : '<ul>';
  const closingMarkup = listType === 'numberType' ? '</ol>' : '</ul>';

  const textLines = splitMainListPoints(text);

  const sublistIndexes = findIndexesOfSublists(
    textLines,
    openingMarkup,
    closingMarkup
  );

  const updatedTextLines = addSublistPointsAsSeparateItems(
    textLines,
    sublistIndexes,
    listType
  );

  // transform main list items to JIRA schema
  const textLinesWithPoints = updatedTextLines.flatMap((line) => {
    const replacer = listType === 'numberType' ? '# ' : '* ';
    return line
      .replaceAll('<li>', replacer)
      .replaceAll('</li>', '\n')
      .split('\n');
  });

  // remove unnecessary markups and strings
  const filteredTextLinesWithBulletPoints = textLinesWithPoints.filter(
    (line) => !['<ul>', '</ul>', '<ol>', '</ol>', ''].includes(line)
  );

  if (
    filteredTextLinesWithBulletPoints[0]?.startsWith('*') ||
    filteredTextLinesWithBulletPoints[0]?.startsWith('#')
  ) {
    // it may happen that test step starts with bullet point, in that case we need to
    // add a blank line after the step number
    filteredTextLinesWithBulletPoints.unshift('');
  }

  return filteredTextLinesWithBulletPoints;
};

const splitMainListPoints = (text: string) => {
  return text
    .replaceAll('<ul><li>', '<ul>\n<li>')
    .replaceAll('</li></ul>', '</li>\n</ul>')
    .replaceAll('</li><ul>', '</li>\n<ul>')
    .replaceAll('</ul><li>', '</ul>\n<li>')
    .replaceAll(/<ol[^>]*><li>/g, '<ol>\n<li>')
    .replaceAll('</li></ol>', '</li>\n</ol>')
    .replaceAll('</li><ol>', '</li>\n<ol>')
    .replaceAll('</ol><li>', '</ol>\n<li>')
    .split('\n');
};

const findIndexesOfSublists = (
  lines: string[],
  openingMarkup: string,
  closingMarkup: string
) => {
  if (lines.length === 3) {
    // this means that list doesn't contain sublist, just normal list
    return [];
  }
  return lines.reduce((indexes: number[], line, index) => {
    if (index === 0 && line === openingMarkup) {
      return indexes;
    }
    if (index === lines.length - 1 && line === closingMarkup) {
      return indexes;
    }
    if (
      lines[index - 1] === openingMarkup &&
      lines[index + 1].includes(closingMarkup)
    ) {
      indexes.push(index);
    }
    return indexes;
  }, []);
};

const addSublistPointsAsSeparateItems = (
  textLines: string[],
  sublistIndexes: number[],
  listType: string
) => {
  const updatedTextLines = [...textLines];

  let totalItemsAdded = 0;

  sublistIndexes.forEach((sublistIndex) => {
    const adjustedIndex = sublistIndex + totalItemsAdded;
    const sublistItem = textLines[sublistIndex];

    if (sublistItem !== null && sublistItem !== undefined) {
      const sublistItemWithBulletpoints = sublistItem
        .replaceAll('</li>', '\n')
        .split('\n')
        .map((item, i) => {
          const replacer = listType === 'numberType' ? `${i + 1}. ` : '** ';
          return item.replace('<li>', replacer);
        });

      updatedTextLines.splice(adjustedIndex, 1, ...sublistItemWithBulletpoints);

      totalItemsAdded += sublistItemWithBulletpoints.length - 1;
    }
  });

  return updatedTextLines;
};
